import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Loader2, Plus, Search, FileDown, History, LogOut, X } from "lucide-react";
import { toast } from "sonner";
import { Logo } from "@/components/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TEAM_TYPES = ["ACT", "PACT", "FACT", "Other approved program"];
const STATUSES = ["Pending", "Active", "Suspended", "Expired", "Cancelled"];

const inputCls =
  "w-full rounded-md border border-slate-300 bg-white px-3.5 py-2.5 text-sm text-slate-900 outline-none transition-colors focus:border-teal-600 focus:ring-2 focus:ring-teal-600/20";
const labelCls = "mb-1.5 block text-sm font-medium text-slate-700";

const STATUS_STYLES = {
  Active: "bg-teal-50 text-teal-700 ring-teal-200",
  Pending: "bg-amber-50 text-amber-700 ring-amber-200",
  Suspended: "bg-orange-50 text-orange-700 ring-orange-200",
  Expired: "bg-slate-100 text-slate-600 ring-slate-300",
  Cancelled: "bg-red-50 text-red-700 ring-red-200",
};

const Pill = ({ status }) => (
  <span className={`inline-block rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ${STATUS_STYLES[status] || ""}`} data-testid={`status-pill-${status.toLowerCase()}`}>
    {status}
  </span>
);

function Login({ onLogin }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const res = await fetch(`${API}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: e.target.email.value.trim(), password: e.target.password.value }),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(typeof d.detail === "string" ? d.detail : "Login failed");
      }
      const data = await res.json();
      onLogin(data.token);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-sm rounded-2xl border border-slate-200 bg-white p-8 shadow-xl shadow-navy/5">
      <h1 className="font-display text-2xl font-bold tracking-tight text-navy">VeraChart Admin</h1>
      <p className="mt-2 text-sm text-slate-500">License management — authorized administrators only.</p>
      <form onSubmit={submit} className="mt-6 space-y-4" data-testid="admin-login-form">
        <div>
          <label htmlFor="admin-email" className={labelCls}>Email</label>
          <input id="admin-email" name="email" type="email" required className={inputCls} data-testid="admin-email-input" autoComplete="username" />
        </div>
        <div>
          <label htmlFor="admin-password" className={labelCls}>Password</label>
          <input id="admin-password" name="password" type="password" required className={inputCls} data-testid="admin-password-input" autoComplete="current-password" />
        </div>
        {error && <p className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700" data-testid="admin-login-error">{error}</p>}
        <button type="submit" disabled={busy} data-testid="admin-login-button"
          className="flex w-full items-center justify-center gap-2 rounded-full bg-navy py-3 font-display text-sm font-semibold text-white transition-colors hover:bg-teal-700 disabled:opacity-60">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : null} Sign In
        </button>
      </form>
    </div>
  );
}

const EMPTY_FORM = { organization_name: "", team_name: "", team_type: "ACT", team_location: "", primary_contact_name: "", primary_contact_email: "", notes: "" };

function LicenseFormModal({ initial, onClose, onSave, saving }) {
  const isEdit = Boolean(initial?.license_id);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-navy/50 p-4 backdrop-blur-sm" role="dialog" aria-modal="true" data-testid="license-form-modal">
      <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl bg-white p-7 shadow-2xl">
        <div className="flex items-center justify-between">
          <h2 className="font-display text-xl font-bold text-navy">{isEdit ? `Edit ${initial.license_id}` : "Create Team License"}</h2>
          <button type="button" onClick={onClose} aria-label="Close" className="rounded-md p-1.5 text-slate-400 hover:text-navy"><X className="h-5 w-5" /></button>
        </div>
        <form onSubmit={onSave} className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <label className={labelCls} htmlFor="lf-org">Organization Name *</label>
            <input id="lf-org" name="organization_name" required defaultValue={initial?.organization_name} maxLength={200} className={inputCls} data-testid="license-form-org" />
          </div>
          <div>
            <label className={labelCls} htmlFor="lf-team">Team Name *</label>
            <input id="lf-team" name="team_name" required defaultValue={initial?.team_name} maxLength={200} className={inputCls} data-testid="license-form-team" />
          </div>
          <div>
            <label className={labelCls} htmlFor="lf-type">Team Type *</label>
            <select id="lf-type" name="team_type" defaultValue={initial?.team_type || "ACT"} className={inputCls} data-testid="license-form-type">
              {TEAM_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div className="sm:col-span-2">
            <label className={labelCls} htmlFor="lf-loc">Team Location</label>
            <input id="lf-loc" name="team_location" defaultValue={initial?.team_location} maxLength={200} className={inputCls} data-testid="license-form-location" />
          </div>
          <div>
            <label className={labelCls} htmlFor="lf-contact">Primary Contact Name</label>
            <input id="lf-contact" name="primary_contact_name" defaultValue={initial?.primary_contact_name} maxLength={200} className={inputCls} data-testid="license-form-contact-name" />
          </div>
          <div>
            <label className={labelCls} htmlFor="lf-email">Primary Contact Email</label>
            <input id="lf-email" name="primary_contact_email" type="email" defaultValue={initial?.primary_contact_email} maxLength={200} className={inputCls} data-testid="license-form-contact-email" />
          </div>
          <div className="sm:col-span-2">
            <label className={labelCls} htmlFor="lf-notes">Internal Notes</label>
            <textarea id="lf-notes" name="notes" rows={2} defaultValue={initial?.notes} maxLength={2000} className={inputCls} data-testid="license-form-notes" />
          </div>
          <div className="sm:col-span-2">
            <button type="submit" disabled={saving} data-testid="license-form-submit"
              className="flex w-full items-center justify-center gap-2 rounded-full bg-teal-600 py-3 font-display text-sm font-semibold text-white transition-colors hover:bg-teal-700 disabled:opacity-60">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : null}
              {isEdit ? "Save Changes" : "Create License (status: Pending)"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Dashboard({ token, onLogout }) {
  const [stats, setStats] = useState(null);
  const [licenses, setLicenses] = useState([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [modal, setModal] = useState(null); // null | {mode:'create'} | {mode:'edit', license} | {mode:'history', license, entries}
  const [saving, setSaving] = useState(false);

  const api = useCallback(async (path, options = {}) => {
    const res = await fetch(`${API}${path}`, {
      ...options,
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}`, ...(options.headers || {}) },
    });
    if (res.status === 401) { onLogout(); throw new Error("Session expired"); }
    if (!res.ok) {
      const d = await res.json().catch(() => ({}));
      throw new Error(typeof d.detail === "string" ? d.detail : "Request failed");
    }
    return res.json();
  }, [token, onLogout]);

  const load = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.set("search", search);
      if (statusFilter) params.set("status", statusFilter);
      const [s, l] = await Promise.all([api("/admin/stats"), api(`/admin/licenses?${params}`)]);
      setStats(s);
      setLicenses(l);
    } catch (err) {
      if (err.message !== "Session expired") toast.error(err.message);
    }
  }, [api, search, statusFilter]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const saveLicense = async (e) => {
    e.preventDefault();
    setSaving(true);
    const val = (n) => e.target.elements[n]?.value?.trim() || "";
    const body = {
      organization_name: val("organization_name"), team_name: val("team_name"), team_type: val("team_type"),
      team_location: val("team_location"), primary_contact_name: val("primary_contact_name"),
      primary_contact_email: val("primary_contact_email"), notes: val("notes"),
    };
    try {
      if (modal.mode === "create") {
        const created = await api("/admin/licenses", { method: "POST", body: JSON.stringify(body) });
        toast.success(`License ${created.license_id} created (Pending)`);
      } else {
        await api(`/admin/licenses/${modal.license.license_id}`, { method: "PATCH", body: JSON.stringify(body) });
        toast.success("License updated");
      }
      setModal(null);
      load();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const doAction = async (license, action) => {
    try {
      await api(`/admin/licenses/${license.license_id}`, { method: "PATCH", body: JSON.stringify({ action }) });
      toast.success(`${license.license_id} → ${action}d`);
      load();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const showHistory = async (license) => {
    try {
      const entries = await api(`/admin/licenses/${license.license_id}/history`);
      setModal({ mode: "history", license, entries });
    } catch (err) {
      toast.error(err.message);
    }
  };

  const downloadCertificate = async (license) => {
    try {
      const res = await fetch(`${API}/admin/licenses/${license.license_id}/certificate.pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Download failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `VeraChart-License-${license.license_id}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err.message);
    }
  };

  const actionsFor = (status) => {
    switch (status) {
      case "Pending": return ["activate", "cancel"];
      case "Active": return ["suspend", "cancel"];
      case "Suspended": return ["activate", "cancel"];
      case "Expired": return ["activate"];
      default: return [];
    }
  };

  return (
    <div data-testid="admin-dashboard">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight text-navy">Team Licenses</h1>
          <p className="mt-1 text-sm text-slate-500">One license per community-based team.</p>
        </div>
        <button type="button" onClick={() => setModal({ mode: "create" })} data-testid="admin-new-license-button"
          className="inline-flex items-center gap-2 rounded-full bg-teal-600 px-5 py-2.5 font-display text-sm font-semibold text-white transition-colors hover:bg-teal-700">
          <Plus className="h-4 w-4" aria-hidden="true" /> New License
        </button>
      </div>

      {stats && (
        <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6" data-testid="admin-stats">
          {[
            ["Active", stats.by_status.Active, "admin-stats-active"],
            ["Pending", stats.by_status.Pending, "admin-stats-pending"],
            ["Suspended", stats.by_status.Suspended, "admin-stats-suspended"],
            ["Expired", stats.by_status.Expired, "admin-stats-expired"],
            ["Organizations", stats.organizations, "admin-stats-orgs"],
            ["Total Licenses", stats.total_licenses, "admin-stats-total"],
          ].map(([label, value, testid]) => (
            <div key={label} className="rounded-xl border border-slate-200 bg-white p-4">
              <p className="font-mono text-2xl font-bold text-navy" data-testid={testid}>{value}</p>
              <p className="mt-1 text-xs text-slate-500">{label}</p>
            </div>
          ))}
        </div>
      )}

      {stats?.teams_per_org?.length > 0 && (
        <div className="mt-4 flex flex-wrap gap-2" data-testid="admin-teams-per-org">
          {stats.teams_per_org.map((o) => (
            <span key={o.organization} className="rounded-full bg-navy px-3.5 py-1.5 text-xs font-medium text-white">
              {o.organization} · {o.teams} team{o.teams === 1 ? "" : "s"}
            </span>
          ))}
        </div>
      )}

      <div className="mt-8 flex flex-col gap-3 sm:flex-row">
        <div className="relative flex-1">
          <Search className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" aria-hidden="true" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search organization, team, license ID, or contact email…"
            className={`${inputCls} pl-10`}
            data-testid="admin-search-input"
          />
        </div>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className={`${inputCls} sm:w-44`} data-testid="admin-status-filter" aria-label="Filter by status">
          <option value="">All statuses</option>
          {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      <div className="mt-5 overflow-x-auto rounded-xl border border-slate-200 bg-white" data-testid="admin-license-table">
        <table className="w-full min-w-[860px] text-left text-sm">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
              <th className="px-4 py-3 font-semibold">License ID</th>
              <th className="px-4 py-3 font-semibold">Organization</th>
              <th className="px-4 py-3 font-semibold">Team</th>
              <th className="px-4 py-3 font-semibold">Type</th>
              <th className="px-4 py-3 font-semibold">Status</th>
              <th className="px-4 py-3 font-semibold">Contact</th>
              <th className="px-4 py-3 font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody>
            {licenses.map((lic) => (
              <tr key={lic.license_id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50/60" data-testid={`license-row-${lic.license_id}`}>
                <td className="px-4 py-3 font-mono text-xs font-bold text-navy">{lic.license_id}</td>
                <td className="px-4 py-3">{lic.organization_name}</td>
                <td className="px-4 py-3">{lic.team_name}</td>
                <td className="px-4 py-3"><span className="rounded bg-slate-100 px-2 py-0.5 font-mono text-xs">{lic.team_type}</span></td>
                <td className="px-4 py-3"><Pill status={lic.license_status} /></td>
                <td className="px-4 py-3 text-xs text-slate-600">{lic.primary_contact_email || "—"}</td>
                <td className="px-4 py-3">
                  <div className="flex flex-wrap items-center gap-1.5">
                    {actionsFor(lic.license_status).map((a) => (
                      <button key={a} type="button" onClick={() => doAction(lic, a)} data-testid={`license-action-${a}-${lic.license_id}`}
                        className={`rounded-full px-2.5 py-1 text-xs font-semibold transition-colors ${a === "cancel" ? "bg-red-50 text-red-700 hover:bg-red-100" : a === "suspend" ? "bg-orange-50 text-orange-700 hover:bg-orange-100" : "bg-teal-50 text-teal-700 hover:bg-teal-100"}`}>
                        {a.charAt(0).toUpperCase() + a.slice(1)}
                      </button>
                    ))}
                    <button type="button" onClick={() => setModal({ mode: "edit", license: lic })} data-testid={`license-edit-${lic.license_id}`} className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-200">Edit</button>
                    <button type="button" onClick={() => showHistory(lic)} data-testid={`license-history-${lic.license_id}`} className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-200" title="View history"><History className="h-3.5 w-3.5" aria-hidden="true" /></button>
                    <button type="button" onClick={() => downloadCertificate(lic)} data-testid={`license-cert-${lic.license_id}`} className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-200" title="Download license certificate PDF"><FileDown className="h-3.5 w-3.5" aria-hidden="true" /></button>
                  </div>
                </td>
              </tr>
            ))}
            {licenses.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-10 text-center text-sm text-slate-400" data-testid="admin-empty-state">No licenses yet. Create the first team license.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {(modal?.mode === "create" || modal?.mode === "edit") && (
        <LicenseFormModal
          initial={modal.mode === "edit" ? modal.license : EMPTY_FORM}
          onClose={() => setModal(null)}
          onSave={saveLicense}
          saving={saving}
        />
      )}

      {modal?.mode === "history" && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-navy/50 p-4 backdrop-blur-sm" role="dialog" aria-modal="true" data-testid="license-history-modal">
          <div className="max-h-[80vh] w-full max-w-lg overflow-y-auto rounded-2xl bg-white p-7 shadow-2xl">
            <div className="flex items-center justify-between">
              <h2 className="font-display text-lg font-bold text-navy">History — {modal.license.license_id}</h2>
              <button type="button" onClick={() => setModal(null)} aria-label="Close" className="rounded-md p-1.5 text-slate-400 hover:text-navy"><X className="h-5 w-5" /></button>
            </div>
            <ul className="mt-4 space-y-3">
              {modal.entries.map((en) => (
                <li key={en.id} className="rounded-lg bg-slate-50 p-3.5 text-sm">
                  <p className="font-semibold text-navy">{en.action}</p>
                  {en.details && <p className="mt-1 text-xs text-slate-600">{en.details}</p>}
                  <p className="mt-1 font-mono text-[11px] text-slate-400">{en.actor} · {new Date(en.created_at).toLocaleString()}</p>
                </li>
              ))}
              {modal.entries.length === 0 && <li className="text-sm text-slate-400">No history yet.</li>}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}

export default function Admin() {
  const [token, setToken] = useState(() => sessionStorage.getItem("vc_admin_token") || "");

  const handleLogin = (t) => {
    sessionStorage.setItem("vc_admin_token", t);
    setToken(t);
  };
  const handleLogout = useCallback(() => {
    sessionStorage.removeItem("vc_admin_token");
    setToken("");
  }, []);

  return (
    <div className="min-h-screen bg-slate-50 font-body text-slate-900">
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex h-[68px] max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
          <Logo />
          <div className="flex items-center gap-4">
            {token && (
              <button type="button" onClick={handleLogout} data-testid="admin-logout-button" className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-600 hover:text-navy">
                <LogOut className="h-4 w-4" aria-hidden="true" /> Sign Out
              </button>
            )}
            <Link to="/" data-testid="admin-back-link" className="inline-flex items-center gap-2 text-sm font-medium text-slate-600 transition-colors hover:text-navy">
              <ArrowLeft className="h-4 w-4" aria-hidden="true" /> Site
            </Link>
          </div>
        </div>
      </header>
      <main className="mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:px-8">
        {token ? <Dashboard token={token} onLogout={handleLogout} /> : <Login onLogin={handleLogin} />}
      </main>
    </div>
  );
}
